/* Dreams by Rusatsi — mobile navigation menu.
   Open/close, focus trap, Escape-to-close, iOS-safe body scroll lock with
   scroll-position restore, and close-on-navigate. Desktop nav is untouched
   by this file; the panel itself is only ever visible below the header's
   900px breakpoint (assets/dbr-mobile-menu.css). */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function DBRMobileMenu(header) {
    this.header = header;
  }

  DBRMobileMenu.prototype.init = function () {
    this.toggle = this.header.querySelector('[data-dbr-menu-toggle]');
    this.menu = this.header.querySelector('[data-dbr-mobile-menu]');
    if (!this.toggle || !this.menu) return;

    /* Moved to a direct child of <body> rather than left nested inside
       <header>: .dbr-header gains backdrop-filter once scrolled
       (.is-scrolled), and per spec any ancestor with backdrop-filter,
       filter, transform, perspective or will-change creates a new
       containing block for position:fixed descendants — which silently
       collapses this panel to the header's own content height instead of
       the viewport. Living at the body level keeps its fixed positioning
       anchored to the real viewport no matter the header's state. */
    if (this.menu.parentNode !== document.body) {
      document.body.appendChild(this.menu);
    }

    this.panel = this.menu.querySelector('[data-dbr-mobile-menu-panel]');
    this.backdrop = this.menu.querySelector('[data-dbr-mobile-menu-backdrop]');
    this.closeButton = this.menu.querySelector('[data-dbr-mobile-menu-close]');
    this.links = Array.prototype.slice.call(this.menu.querySelectorAll('[data-dbr-mobile-menu-link]'));

    this.isOpen = false;
    this.scrollY = 0;
    this.previouslyFocused = null;

    this._onToggleClick = this.onToggleClick.bind(this);
    this._onCloseClick = this.close.bind(this);
    this._onBackdropClick = this.onBackdropClick.bind(this);
    this._onLinkClick = this.close.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    this._onResize = this.onResize.bind(this);

    this.toggle.addEventListener('click', this._onToggleClick);
    if (this.closeButton) this.closeButton.addEventListener('click', this._onCloseClick);
    if (this.backdrop) this.backdrop.addEventListener('click', this._onBackdropClick);
    this.links.forEach(function (link) {
      link.addEventListener('click', this._onLinkClick);
    }, this);
    /* The panel, its close button and the hamburger trigger are all hidden
       (display:none) above the header's own 900px breakpoint. Without this,
       resizing or rotating past that width while the menu is open leaves
       the body scroll lock on with no visible control left that can call
       close(). */
    window.addEventListener('resize', this._onResize, { passive: true });
  };

  DBRMobileMenu.prototype.onToggleClick = function () {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  DBRMobileMenu.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.previouslyFocused = document.activeElement;

    this.scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -this.scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.classList.add('dbr-scroll-locked');

    this.menu.setAttribute('aria-hidden', 'false');
    this.menu.classList.add('is-open');
    this.header.classList.add('is-menu-open');
    this.toggle.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', this._onKeydown);

    var self = this;
    window.requestAnimationFrame(function () {
      if (self.closeButton) self.closeButton.focus();
    });
  };

  DBRMobileMenu.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.menu.setAttribute('aria-hidden', 'true');
    this.menu.classList.remove('is-open');
    this.header.classList.remove('is-menu-open');
    this.toggle.setAttribute('aria-expanded', 'false');

    var scrollY = this.scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.classList.remove('dbr-scroll-locked');
    window.scrollTo(0, scrollY);

    document.removeEventListener('keydown', this._onKeydown);

    var restoreTarget = this.previouslyFocused && this.previouslyFocused.focus ? this.previouslyFocused : this.toggle;
    restoreTarget.focus();
    this.previouslyFocused = null;
  };

  DBRMobileMenu.prototype.onBackdropClick = function (event) {
    if (event.target === this.backdrop) this.close();
  };

  DBRMobileMenu.prototype.onResize = function () {
    if (this.isOpen && window.innerWidth > 900) this.close();
  };

  DBRMobileMenu.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  };

  /* Focus stays inside the panel while open — Tab from the last focusable
     element wraps to the first, Shift+Tab from the first wraps to the last. */
  DBRMobileMenu.prototype.trapFocus = function (event) {
    if (!this.panel) return;
    var focusable = Array.prototype.slice
      .call(this.panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  DBRMobileMenu.prototype.destroy = function () {
    if (this.isOpen) this.close();
    if (this.toggle) this.toggle.removeEventListener('click', this._onToggleClick);
    if (this.closeButton) this.closeButton.removeEventListener('click', this._onCloseClick);
    if (this.backdrop) this.backdrop.removeEventListener('click', this._onBackdropClick);
    var self = this;
    this.links.forEach(function (link) {
      link.removeEventListener('click', self._onLinkClick);
    });
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
    /* The section re-render that triggers destroy() creates a fresh
       .dbr-mobile-menu inside the new header markup — this instance's copy
       was relocated to <body> in init() and must be removed explicitly or
       it would linger as an orphaned duplicate. */
    if (this.menu && this.menu.parentNode) {
      this.menu.parentNode.removeChild(this.menu);
    }
  };

  function initHeader(header) {
    if (!header) return;
    /* Destroy any previous instance first so a Shopify Theme Editor reload
       always leaves exactly one live listener set, never a stacked
       duplicate — same convention as dbr-home-curved-gallery.js. */
    if (header._dbrMobileMenu) {
      header._dbrMobileMenu.destroy();
      header._dbrMobileMenu = null;
    }
    var menu = new DBRMobileMenu(header);
    menu.init();
    header._dbrMobileMenu = menu;
  }

  document.querySelectorAll('[data-dbr-header]').forEach(initHeader);

  document.addEventListener('shopify:section:load', function (evt) {
    var header = evt.target.querySelector ? evt.target.querySelector('[data-dbr-header]') : null;
    if (header) initHeader(header);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var header = evt.target.querySelector ? evt.target.querySelector('[data-dbr-header]') : null;
    if (header && header._dbrMobileMenu) {
      header._dbrMobileMenu.destroy();
      header._dbrMobileMenu = null;
    }
  });
})();
